import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  resource,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { handleRequestError } from '@softline/application';
import { authorizationFn } from '@softline/auth';
import { DefaultStore, Store, ValidationStore } from '@softline/core';
import {
  RadioGroupDirective,
  RadioOptionComponent,
  ResolvableModal,
  ButtonComponent,
  BadgeComponent,
} from '@softline/ui-core';
import moment from 'moment';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take } from 'rxjs/operators';
import { TimestampCode } from '../../../data/timestamp-code.model';
import { Timestamp } from '../../../data/timestamp.model';
import { TimestampCodeService } from '../../../services/timestamp-code.service';
import {
  SOFTLINE_PERMISSION_TIMESTAMP_CUSTOM,
  SOFTLINE_PERMISSION_TIMESTAMP_SELECT_EMPLOYEE,
} from '../../../stempelerfassung.permissions';
import {
  SOFTLINE_FEATURE_TIMESTAMP,
  SOFTLINE_FEATURE_TIMESTAMP_CODE,
  SOFTLINE_FEATURE_TIMESTAMP_EMPLOYEE,
  TimestampCodeStore,
  TimestampEmployeeStore,
  TimestampStore,
} from '../../../stempelerfassung.shared';

import {
  DataViewDirective,
  DateTimeInputComponent,
  EntityInputComponent,
  EntityPickerComponent,
  IconComponent,
  InputComponent,
  LoadingSpinnerComponent,
  MasterDetailComponent,
  QueryViewDirective,
  SelectDsComponent,
  SelectDsOptionComponent,
  TranslatePipe,
  ValidationContainerComponent,
} from '@softline/ui-core';
@Component({
  selector: 'soft-record-timestamp-dialog',
  templateUrl: './record-timestamp-dialog.component.html',
  styleUrls: ['./record-timestamp-dialog.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DateTimeInputComponent,
    EntityInputComponent,
    IconComponent,
    InputComponent,
    LoadingSpinnerComponent,
    MasterDetailComponent,
    RadioGroupDirective,
    RadioOptionComponent,
    SelectDsComponent,
    SelectDsOptionComponent,
    TranslatePipe,
    ValidationContainerComponent,
    DataViewDirective,
    EntityPickerComponent,
    QueryViewDirective,
    ButtonComponent,
    BadgeComponent,
  ],
  providers: [TimestampCodeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecordTimestampDialogComponent
  extends ResolvableModal<Timestamp>
  implements OnInit, OnDestroy
{
  private store = inject(Store);
  private validationSubscription?: Subscription;
  private injector = inject(Injector);
  private readonly isAuthorized = authorizationFn();

  readonly #service = inject(TimestampCodeService);

  form: UntypedFormGroup = new UntypedFormGroup(
    {
      employee: new UntypedFormControl(undefined),
      flag: new UntypedFormControl('arrival'),
      code: new UntypedFormControl(undefined),
      timestamp: new UntypedFormControl(moment().toISOString()),
    },
    { asyncValidators: (control) => this.validateForm(this.store, control) }
  );

  codes$: Observable<readonly TimestampCode[]> = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP_CODE,
    TimestampCodeStore.getters.all
  );

  employees$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP_EMPLOYEE,
    TimestampEmployeeStore.getters.all
  );

  validation$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP,
    ValidationStore.getters.validation.validation
  );

  templateLoading$ = this.store.observe(
    SOFTLINE_FEATURE_TIMESTAMP,
    TimestampStore.getters.loading
  );

  readonly canModifyTime = this.isAuthorized(SOFTLINE_PERMISSION_TIMESTAMP_CUSTOM);
  readonly canModifyEmployee = this.isAuthorized(SOFTLINE_PERMISSION_TIMESTAMP_SELECT_EMPLOYEE);

  readonly flag = toSignal(
    this.form.get('flag')!.valueChanges.pipe(distinctUntilChanged()),
    {
      initialValue: 'undefined',
    }
  );

  readonly codes = resource({
    params: () => this.flag(),
    loader: async (params) => {
      if (!params?.params) return [];
      try {
        return await firstValueFrom(this.#service.retrieveCodes(params.params));
      } catch (e) {
        handleRequestError(e, this.injector);
        return [];
      }
    },
  });

  protected readonly codeDisabled = computed(() => {
    const codes = this.codes.value();
    return !codes || codes.length <= 1;
  });

  constructor() {
    super();
  }

  async ngOnInit(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_TIMESTAMP,
      DefaultStore.actions.default.load,
      {}
    );

    this.validationSubscription = this.form.valueChanges
      .pipe(debounceTime(100))
      .subscribe(async (entity) => {
        await this.store.dispatch(
          SOFTLINE_FEATURE_TIMESTAMP,
          ValidationStore.actions.validation.validate,
          { entity }
        );
      });

    this.store
      .observe(SOFTLINE_FEATURE_TIMESTAMP, DefaultStore.getters.default.default)
      .pipe(
        filter((o) => !!o),
        take(1)
      )
      .subscribe((o) => this.form.patchValue(o));
  }

  ngOnDestroy(): void {
    if (this.validationSubscription && !this.validationSubscription.closed) {
      this.validationSubscription.unsubscribe();
    }
    this.validationSubscription = undefined;
  }

  onSubmit(timestamp: Timestamp): void {
    this.resolve(timestamp);
  }

  private validateForm(store: Store, control: AbstractControl): Observable<any> {
    return store
      .observe(SOFTLINE_FEATURE_TIMESTAMP, ValidationStore.getters.validation.valid)
      .pipe(map((o) => ({ isValid: o })));
  }

  filterCodes(code: TimestampCode, flag: 'arrival' | 'departure' | undefined): boolean {
    return code.flag === flag;
  }

  onFlagChange() {
    this.form.patchValue({ code: null });
  }
}
